/**
 * ============================================================================
 * USER / TENANT PAGE: Messages & Transmission Audit Log (Sheets 26 & 31)
 * ============================================================================
 * Purpose:
 *   Tenant workspace interface for viewing incoming WhatsApp replies
 *   (`tbl_incoming_messages`) and centralized message delivery status logs
 *   (`tbl_message_log`).
 *
 * Database Scope:
 *   Tenant Database (`digi_user_<id>`)
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from "react";
import { messageService } from "../../services";

// SVG Icons
const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
    />
  </svg>
);

const FunnelIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
    />
  </svg>
);

const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
);

const ChevronLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

const ArrowUpRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" />
  </svg>
);

const ArrowDownLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 4.5-15 15m0 0h11.25m-11.25 0V8.25" />
  </svg>
);

const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const PhoneIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
    />
  </svg>
);

const ChatBubbleLeftRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"
    />
  </svg>
);

const PaperAirplaneIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
    />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
    />
  </svg>
);

const ExclamationCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
    />
  </svg>
);

export default function MessageLogView() {
  const [activeTab, setActiveTab] = useState("logs"); // 'logs' | 'incoming'

  // Logs State
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const [logLimit, setLogLimit] = useState(10);
  const [logSearch, setLogSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [logTotal, setLogTotal] = useState(0);

  // Incoming Messages State
  const [incoming, setIncoming] = useState([]);
  const [loadingIncoming, setLoadingIncoming] = useState(false);
  const [incPage, setIncPage] = useState(1);
  const [incLimit, setIncLimit] = useState(10);
  const [incSearch, setIncSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [incTotal, setIncTotal] = useState(0);

  // Global Stats
  const [stats, setStats] = useState({
    totalLogs: 0,
    delivered: 0,
    failed: 0,
    inbound: 0,
  });

  // Message Detail Modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailModalType, setDetailModalType] = useState(null); // 'log' | 'incoming'

  // Load High-Level Stats
  const loadStats = useCallback(async () => {
    try {
      const [totalRes, delivRes, readRes, failRes, incRes] = await Promise.allSettled([
        messageService.logs.get({ limit: 1 }),
        messageService.logs.get({ limit: 1, str_status: "DELIVERED" }),
        messageService.logs.get({ limit: 1, str_status: "READ" }),
        messageService.logs.get({ limit: 1, str_status: "FAILED" }),
        messageService.incoming.get({ limit: 1 }),
      ]);

      const totalLogs =
        totalRes.status === "fulfilled" && totalRes.value?.pagination?.total != null
          ? totalRes.value.pagination.total
          : 0;

      const delivCount =
        delivRes.status === "fulfilled" ? delivRes.value?.pagination?.total || 0 : 0;
      const readCount =
        readRes.status === "fulfilled" ? readRes.value?.pagination?.total || 0 : 0;
      const deliveredTotal = delivCount + readCount;

      const failCount =
        failRes.status === "fulfilled" && failRes.value?.pagination?.total != null
          ? failRes.value.pagination.total
          : 0;

      const inboundCount =
        incRes.status === "fulfilled" && incRes.value?.pagination?.total != null
          ? incRes.value.pagination.total
          : 0;

      setStats({
        totalLogs,
        delivered: deliveredTotal,
        failed: failCount,
        inbound: inboundCount,
      });
    } catch (err) {
      console.error("Failed to load message stats:", err);
    }
  }, []);

  // Load Message Delivery Logs
  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = {
        page: logPage,
        limit: logLimit,
      };
      if (logSearch.trim()) params.search = logSearch.trim();
      if (statusFilter !== "all") params.str_status = statusFilter;
      if (channelFilter !== "all") params.str_source_type = channelFilter;

      const res = await messageService.logs.get(params);
      if (res && res.success) {
        setLogs(Array.isArray(res.data) ? res.data : []);
        setLogTotal(res.pagination?.total || (res.data ? res.data.length : 0));
      } else if (Array.isArray(res)) {
        setLogs(res);
        setLogTotal(res.length);
      } else {
        setLogs([]);
        setLogTotal(0);
      }
    } catch (err) {
      console.error("Failed to load message logs:", err);
      setLogs([]);
      setLogTotal(0);
    } finally {
      setLoadingLogs(false);
    }
  }, [logPage, logLimit, logSearch, statusFilter, channelFilter]);

  // Load Incoming Customer Messages
  const loadIncoming = useCallback(async () => {
    setLoadingIncoming(true);
    try {
      const params = {
        page: incPage,
        limit: incLimit,
      };
      if (incSearch.trim()) params.search = incSearch.trim();
      if (typeFilter !== "all") params.str_message_type = typeFilter;

      const res = await messageService.incoming.get(params);
      if (res && res.success) {
        setIncoming(Array.isArray(res.data) ? res.data : []);
        setIncTotal(res.pagination?.total || (res.data ? res.data.length : 0));
      } else if (Array.isArray(res)) {
        setIncoming(res);
        setIncTotal(res.length);
      } else {
        setIncoming([]);
        setIncTotal(0);
      }
    } catch (err) {
      console.error("Failed to load incoming messages:", err);
      setIncoming([]);
      setIncTotal(0);
    } finally {
      setLoadingIncoming(false);
    }
  }, [incPage, incLimit, incSearch, typeFilter]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === "logs") {
      loadLogs();
    } else {
      loadIncoming();
    }
  }, [activeTab, loadLogs, loadIncoming]);

  const handleRefresh = async () => {
    await Promise.all([loadStats(), activeTab === "logs" ? loadLogs() : loadIncoming()]);
  };

  // Helper status badge styling
  const renderStatusBadge = (status) => {
    const s = (status || "SENT").toUpperCase();
    if (s === "READ") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
          <CheckCircleIcon className="w-3.5 h-3.5 mr-1 text-emerald-600 dark:text-emerald-400" />
          Read
        </span>
      );
    }
    if (s === "DELIVERED") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
          <CheckCircleIcon className="w-3.5 h-3.5 mr-1 text-blue-600 dark:text-blue-400" />
          Delivered
        </span>
      );
    }
    if (s === "SENT") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300">
          <PaperAirplaneIcon className="w-3.5 h-3.5 mr-1 text-cyan-600 dark:text-cyan-400" />
          Sent
        </span>
      );
    }
    if (s === "QUEUED") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
          Queued
        </span>
      );
    }
    if (s === "FAILED") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">
          <ExclamationCircleIcon className="w-3.5 h-3.5 mr-1 text-rose-600 dark:text-rose-400" />
          Failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
        {s}
      </span>
    );
  };

  const totalLogPages = Math.ceil(logTotal / logLimit) || 1;
  const totalIncPages = Math.ceil(incTotal / incLimit) || 1;

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header section with Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-blue-600 bg-clip-text text-transparent mb-2">
              Message Audit & Delivery Logs
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
              Monitor real-time message transmission statuses (SENT, DELIVERED, READ, FAILED) across campaigns and inbound customer chat replies
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-sm shadow-blue-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${loadingLogs || loadingIncoming ? "animate-spin" : ""}`} />
              Refresh Logs
            </button>
          </div>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white font-mono">
            {stats.totalLogs.toLocaleString()}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Total Messages Logged
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-green-600 font-mono">
            {stats.delivered.toLocaleString()}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Delivered & Read
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-rose-500 font-mono">
            {stats.failed.toLocaleString()}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Failed / Undelivered
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-purple-600 font-mono">
            {stats.inbound.toLocaleString()}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Inbound Customer Replies
          </div>
        </div>
      </div>

      {/* Modern Tabs */}
      <div className="flex space-x-2 border-b border-black/5 dark:border-white/10 pb-2">
        <button
          onClick={() => setActiveTab("logs")}
          className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all cursor-pointer ${
            activeTab === "logs"
              ? "bg-[#007AFF] text-white shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          📊 Delivery Audit Log (tbl_message_log)
        </button>
        <button
          onClick={() => setActiveTab("incoming")}
          className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all cursor-pointer ${
            activeTab === "incoming"
              ? "bg-[#007AFF] text-white shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          📥 Inbound Customer Replies (tbl_incoming_messages)
        </button>
      </div>

      {/* Tab 1: Delivery Audit Log */}
      {activeTab === "logs" && (
        <div className="space-y-6">
          {/* Filters Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
              <div className="relative flex-1 min-w-[240px]">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search recipient number, message ID..."
                  value={logSearch}
                  onChange={(e) => {
                    setLogSearch(e.target.value);
                    setLogPage(1);
                  }}
                  className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <FunnelIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setLogPage(1);
                  }}
                  className="pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="all">All Statuses</option>
                  <option value="READ">Read</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="SENT">Sent</option>
                  <option value="QUEUED">Queued</option>
                  <option value="FAILED">Failed</option>
                </select>
              </div>

              {/* Channel Filter */}
              <div className="relative">
                <select
                  value={channelFilter}
                  onChange={(e) => {
                    setChannelFilter(e.target.value);
                    setLogPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="all">All Channels</option>
                  <option value="CAMPAIGN">Campaign</option>
                  <option value="API">API Gateway</option>
                  <option value="CHAT">Live Chat</option>
                  <option value="BOT">Chatbot</option>
                  <option value="WEBHOOK">Webhook</option>
                </select>
              </div>

              {/* Per Page */}
              <div className="relative">
                <select
                  value={logLimit}
                  onChange={(e) => {
                    setLogLimit(Number(e.target.value));
                    setLogPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {loadingLogs ? (
              <div className="py-16 text-center">
                <ArrowPathIcon className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-3" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">Loading transmission logs...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="py-16 px-4 text-center">
                <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
                  <PaperAirplaneIcon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No Message Delivery Logs Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto text-sm">
                  {logSearch || statusFilter !== "all" || channelFilter !== "all"
                    ? "No log entries match your selected criteria. Try adjusting your search query or filters."
                    : "No messages have been logged yet. Launch a broadcast campaign or send an API message to see real-time delivery telemetry."}
                </p>
                {(logSearch || statusFilter !== "all" || channelFilter !== "all") && (
                  <button
                    onClick={() => {
                      setLogSearch("");
                      setStatusFilter("all");
                      setChannelFilter("all");
                      setLogPage(1);
                    }}
                    className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl text-xs font-semibold cursor-pointer transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/60">
                      <tr>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-14">
                          #
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Message ID & Channel
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Recipient
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Direction
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Delivery Status
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Timestamp
                        </th>
                        <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {logs.map((row, index) => {
                        const msgId = row.str_message_id || `MSG_${index + 1}`;
                        const channel = row.str_source_type || "CAMPAIGN";
                        const recipient = row.str_recipient_number || "-";
                        const direction = row.str_direction || "OUTGOING";
                        const dateStr = row.dttm_created_on
                          ? new Date(row.dttm_created_on).toLocaleString()
                          : "-";

                        return (
                          <tr
                            key={row._id || row.str_message_id || index}
                            className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-400">
                              {(logPage - 1) * logLimit + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center font-bold">
                                  <PaperAirplaneIcon className="w-4 h-4" />
                                </div>
                                <div>
                                  <div className="font-semibold text-blue-600 dark:text-blue-400 font-mono text-xs">
                                    {msgId}
                                  </div>
                                  <div className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 mt-0.5">
                                    {channel}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <PhoneIcon className="w-3.5 h-3.5 text-gray-400" />
                                <span className="font-mono text-xs text-gray-900 dark:text-white font-medium">
                                  {recipient}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-1 text-xs">
                                {direction === "OUTGOING" ? (
                                  <>
                                    <ArrowUpRightIcon className="w-3.5 h-3.5 text-blue-500" />
                                    <span className="text-gray-700 dark:text-gray-300">Outgoing</span>
                                  </>
                                ) : (
                                  <>
                                    <ArrowDownLeftIcon className="w-3.5 h-3.5 text-emerald-500" />
                                    <span className="text-gray-700 dark:text-gray-300">Incoming</span>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {renderStatusBadge(row.str_status)}
                              {row.str_error_code && (
                                <div className="text-[10px] font-mono text-rose-500 mt-1">
                                  Code: {row.str_error_code}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                              {dateStr}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => {
                                  setSelectedItem(row);
                                  setDetailModalType("log");
                                }}
                                className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors cursor-pointer"
                                title="Inspect message telemetry"
                              >
                                <EyeIcon className="w-4 h-4 mr-1" />
                                Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Showing{" "}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {logTotal === 0 ? 0 : (logPage - 1) * logLimit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {Math.min(logPage * logLimit, logTotal)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-gray-900 dark:text-white">{logTotal}</span> results
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <button
                      onClick={() => setLogPage((p) => Math.max(1, p - 1))}
                      disabled={logPage === 1}
                      className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    {Array.from({ length: Math.min(5, totalLogPages) }, (_, i) => {
                      let pageNumber;
                      if (totalLogPages <= 5) {
                        pageNumber = i + 1;
                      } else if (logPage <= 3) {
                        pageNumber = i + 1;
                      } else if (logPage >= totalLogPages - 2) {
                        pageNumber = totalLogPages - 4 + i;
                      } else {
                        pageNumber = logPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setLogPage(pageNumber)}
                          className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                            logPage === pageNumber
                              ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm"
                              : "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setLogPage((p) => Math.min(totalLogPages, p + 1))}
                      disabled={logPage === totalLogPages || totalLogPages === 0}
                      className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tab 2: Inbound Customer Replies */}
      {activeTab === "incoming" && (
        <div className="space-y-6">
          {/* Filters Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
              <div className="relative flex-1 min-w-[240px]">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customer number or message text..."
                  value={incSearch}
                  onChange={(e) => {
                    setIncSearch(e.target.value);
                    setIncPage(1);
                  }}
                  className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              {/* Message Type Filter */}
              <div className="relative">
                <FunnelIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setIncPage(1);
                  }}
                  className="pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="all">All Message Types</option>
                  <option value="text">Text</option>
                  <option value="image">Image</option>
                  <option value="document">Document</option>
                  <option value="audio">Audio / Voice</option>
                  <option value="video">Video</option>
                </select>
              </div>

              {/* Per Page */}
              <div className="relative">
                <select
                  value={incLimit}
                  onChange={(e) => {
                    setIncLimit(Number(e.target.value));
                    setIncPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>
          </div>

          {/* Incoming Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {loadingIncoming ? (
              <div className="py-16 text-center">
                <ArrowPathIcon className="w-8 h-8 mx-auto text-purple-500 animate-spin mb-3" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">Loading customer replies...</p>
              </div>
            ) : incoming.length === 0 ? (
              <div className="py-16 px-4 text-center">
                <div className="w-16 h-16 bg-purple-50 dark:bg-purple-900/20 text-purple-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
                  <ChatBubbleLeftRightIcon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No Inbound Messages Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto text-sm">
                  {incSearch || typeFilter !== "all"
                    ? "No incoming customer replies match your filter."
                    : "No inbound messages received yet. Replies from customers to your registered WhatsApp numbers will appear here automatically."}
                </p>
                {(incSearch || typeFilter !== "all") && (
                  <button
                    onClick={() => {
                      setIncSearch("");
                      setTypeFilter("all");
                      setIncPage(1);
                    }}
                    className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl text-xs font-semibold cursor-pointer transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/60">
                      <tr>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-14">
                          #
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Message Content
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Received At
                        </th>
                        <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {incoming.map((row, index) => {
                        const customer = row.str_recipient_number || "-";
                        const content = row.str_message || "(Empty or media message)";
                        const msgType = row.str_message_type || "text";
                        const dateStr = row.dttm_created_on
                          ? new Date(row.dttm_created_on).toLocaleString()
                          : "-";

                        return (
                          <tr
                            key={row._id || row.str_message_id || index}
                            className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-400">
                              {(incPage - 1) * incLimit + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-600 flex items-center justify-center font-bold text-xs">
                                  {customer.slice(-2) || "WA"}
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-900 dark:text-white font-mono text-xs">
                                    {customer}
                                  </div>
                                  <div className="text-[11px] text-gray-400 font-mono">
                                    {row.str_message_id?.slice(0, 16) || "ID: -"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="max-w-md bg-gray-50 dark:bg-gray-700/40 rounded-xl px-3 py-2 border border-gray-200/60 dark:border-gray-600/40">
                                <p className="text-xs text-gray-900 dark:text-white line-clamp-2 leading-relaxed">
                                  {content}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 capitalize">
                                {msgType}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                              {dateStr}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => {
                                  setSelectedItem(row);
                                  setDetailModalType("incoming");
                                }}
                                className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-xs font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors cursor-pointer"
                                title="View full message"
                              >
                                <EyeIcon className="w-4 h-4 mr-1" />
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Showing{" "}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {incTotal === 0 ? 0 : (incPage - 1) * incLimit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {Math.min(incPage * incLimit, incTotal)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-gray-900 dark:text-white">{incTotal}</span> results
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <button
                      onClick={() => setIncPage((p) => Math.max(1, p - 1))}
                      disabled={incPage === 1}
                      className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    {Array.from({ length: Math.min(5, totalIncPages) }, (_, i) => {
                      let pageNumber;
                      if (totalIncPages <= 5) {
                        pageNumber = i + 1;
                      } else if (incPage <= 3) {
                        pageNumber = i + 1;
                      } else if (incPage >= totalIncPages - 2) {
                        pageNumber = totalIncPages - 4 + i;
                      } else {
                        pageNumber = incPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setIncPage(pageNumber)}
                          className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                            incPage === pageNumber
                              ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm"
                              : "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setIncPage((p) => Math.min(totalIncPages, p + 1))}
                      disabled={incPage === totalIncPages || totalIncPages === 0}
                      className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Message Inspection / Detail Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-200 dark:border-gray-700 transition-all">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4 mb-5">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {detailModalType === "log" ? "Message Audit Telemetry" : "Inbound Customer Message"}
                </h3>
                <p className="text-xs text-gray-500 font-mono mt-0.5">
                  ID: {selectedItem.str_message_id || "N/A"}
                </p>
              </div>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Telemetry Summary Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-xl">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase font-semibold">
                    Recipient / Customer
                  </div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white font-mono mt-1">
                    {selectedItem.str_recipient_number || "N/A"}
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-xl">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase font-semibold">
                    Status
                  </div>
                  <div className="mt-1">
                    {renderStatusBadge(selectedItem.str_status)}
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-xl">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase font-semibold">
                    Channel / Type
                  </div>
                  <div className="text-xs font-semibold text-gray-900 dark:text-white capitalize mt-1">
                    {selectedItem.str_source_type || selectedItem.str_message_type || "Standard"}
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-xl">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase font-semibold">
                    Direction
                  </div>
                  <div className="text-xs font-semibold text-gray-900 dark:text-white capitalize mt-1">
                    {selectedItem.str_direction || "Outgoing"}
                  </div>
                </div>
              </div>

              {/* Message Content preview if available */}
              {selectedItem.str_message && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Message Content
                  </label>
                  <div className="bg-emerald-50/60 dark:bg-gray-700/60 border border-emerald-200 dark:border-gray-600 rounded-xl p-3 text-xs text-gray-900 dark:text-white whitespace-pre-wrap leading-relaxed">
                    {selectedItem.str_message}
                  </div>
                </div>
              )}

              {/* Error Details if Failed */}
              {(selectedItem.str_error_code || selectedItem.str_error_message) && (
                <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-3">
                  <div className="flex items-center space-x-2 text-rose-700 dark:text-rose-400 font-semibold text-xs mb-1">
                    <ExclamationCircleIcon className="w-4 h-4" />
                    <span>Error Code: {selectedItem.str_error_code || "Unknown"}</span>
                  </div>
                  <p className="text-xs text-rose-600 dark:text-rose-300">
                    {selectedItem.str_error_message || "No error details reported by provider."}
                  </p>
                </div>
              )}

              {/* Additional Meta info */}
              <div className="text-[11px] text-gray-400 space-y-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                {selectedItem.str_campaign_id && (
                  <div>
                    Campaign ID: <span className="font-mono text-gray-600 dark:text-gray-300">{selectedItem.str_campaign_id}</span>
                  </div>
                )}
                {selectedItem.str_phone_number_id && (
                  <div>
                    Phone Number ID: <span className="font-mono text-gray-600 dark:text-gray-300">{selectedItem.str_phone_number_id}</span>
                  </div>
                )}
                <div>
                  Created:{" "}
                  <span className="text-gray-600 dark:text-gray-300">
                    {selectedItem.dttm_created_on
                      ? new Date(selectedItem.dttm_created_on).toLocaleString()
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedItem(null)}
                className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl text-xs font-semibold cursor-pointer transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
